import SelectInput from '@/components/ui/select-input';
import { Level, Location } from '@/core/entities';
import { CircularProgress, InputAdornment, Stack, capitalize } from '@mui/material';
import { ChangeEvent, FunctionComponent, ReactElement, useMemo } from 'react';
import { ALL_LOCATIONS } from './utils';

interface LocationFilterProps {
	level?: Level;
	locations?: Location[];
	selectedLocation?: string;
	setLocation: (location?: Location) => void;
	disabled?: boolean;
	isLoadingOptions?: boolean;
	isLoadingMoreOptions?: boolean;
	loadMoreOptions?: () => void;
}

const LocationFilter: FunctionComponent<LocationFilterProps> = ({
	level,
	locations,
	selectedLocation,
	setLocation,
	disabled,
	isLoadingOptions,
	isLoadingMoreOptions,
	loadMoreOptions,
}): ReactElement => {
	const locationOptions = useMemo(
		() => [
			{ value: ALL_LOCATIONS, label: ALL_LOCATIONS },
			...(locations?.map((location) => ({ value: location.id, label: location.name })) || []),
		],
		[locations]
	);

	const onLocationChange = (locationId: string) => {
		const location =
			locationId === ALL_LOCATIONS
				? undefined
				: locations?.find((location) => location.id === locationId);
		setLocation(location);
	};

	const optionsLoaderProps = {
		endAdornment: (
			<InputAdornment position="end" sx={{ mr: 3 }}>
				<CircularProgress size={20} />
			</InputAdornment>
		),
	};

	if (!locationOptions) return <></>;

	return (
		<SelectInput
			variant="filled"
			fullWidth
			label={capitalize(level?.name || '')}
			options={locationOptions}
			value={selectedLocation || ALL_LOCATIONS}
			disabled={disabled}
			onChange={(event: ChangeEvent<HTMLInputElement>) => onLocationChange(event.target.value)}
			InputProps={isLoadingOptions ? optionsLoaderProps : undefined}
			onScroll={loadMoreOptions}
			moreOptionsLoadingIndicator={
				isLoadingMoreOptions ? (
					<Stack direction="column" alignItems="center" mt={1}>
						<CircularProgress size={20} />
					</Stack>
				) : undefined
			}
		/>
	);
};

export default LocationFilter;
